import { useEffect, useMemo, useState } from "react";

import { faRefresh } from "@fortawesome/pro-regular-svg-icons";
import { endOfToday, startOfToday, sub } from "date-fns";
import { twJoin } from "tailwind-merge";
import { useDebounce } from "use-debounce";

import { SubHeader, SubHeaderFilter } from "src/flow/SubHeader";
import { useJobs } from "src/jobs/api/queries";
import { tableWidth } from "src/layout/constants";
import {
  PerformanceSource,
  PerformanceSourcePill,
} from "src/performance/ApiJobsPill";
import { DecisionsChart } from "src/performance/charts/DecisionsChart";
import { ErrorsChart } from "src/performance/charts/ErrorsChart";
import { LatencyChart } from "src/performance/charts/LatencyChart";
import { useMetricsRefresh } from "src/performance/MetricsRefresh";
import { OverviewPane } from "src/performance/OverviewPane";
import { useDecisions } from "src/performance/queries";
import { useAvailableVersions } from "src/performance/useAvailableVersions";
import { useVersionPicker } from "src/performance/useVersionPicker";
import { computeSelectedVersions } from "src/performance/utils";
import { useFlowContext } from "src/router/routerContextHooks";
import { formatDate } from "src/utils/datetime";
import { useTimeWindow } from "src/utils/timeWindow";

/**
 * Iceberg Performance Dashboard
 *
 * Key differences from Aurora implementation:
 * - Skip useAvailableVersions call
 * - All queries fire immediately in parallel with no versions parameter
 * - Available versions extracted from decisionsQuery response
 * - 3 states for version selection:
 * - When user selects all versions: pass undefined (omit versions parameter for API call) -> BE returns all metrics for all_flow_versions
 * - When user selects subset: pass array of selected versions
 * - When user deselects all versions: disable queries on FE
 */
export const PerformanceContentIceberg: React.FC = () => {
  const { flow, workspace } = useFlowContext();
  const { refresh, refreshing, dataUpdatedAt } = useMetricsRefresh();

  const { dateRangePickerValue, onDateRangePickerChange, timeWindow } =
    useTimeWindow(
      {
        from: sub(startOfToday(), { weeks: 1 }),
        to: endOfToday(),
      },
      "performance-time-window",
    );

  const [source, setSource] = useState<PerformanceSource>(
    PerformanceSource.API,
  );

  // Version selection state: undefined = fetch all, [] = fetch none, [v1, v2] = specific subset
  const [selectedVersions, setSelectedVersions] = useState<
    string[] | undefined
  >(undefined);

  // Debounce version selection to prevent rapid filter changed from causing multiple API requests
  const [debouncedSelectedVersions, { flush: flushDebouncedVersions }] =
    useDebounce(selectedVersions, 500, {
      trailing: true,
    });

  const filters = {
    timeWindow,
    flowId: flow.id,
    versions: debouncedSelectedVersions,
  };

  const shouldFetch =
    debouncedSelectedVersions === undefined ||
    debouncedSelectedVersions.length > 0;

  const decisions = useDecisions(workspace.base_url, filters, {
    enabled: shouldFetch,
  });

  // Extract available versions from decisions data with race condition prevention
  const allAvailableVersions = useAvailableVersions({
    decisionsData: decisions.data,
    timeWindow,
    selectedVersions: debouncedSelectedVersions,
  });

  // Reset version selection when time window changes
  // Flush debounce immediately to prevent stale version filter on new time window
  useEffect(() => {
    setSelectedVersions(undefined);
    flushDebouncedVersions();
  }, [timeWindow, flushDebouncedVersions]);

  // computer version picker related values
  const { colors, options } = useVersionPicker({
    flowVersions: flow.versions,
    allAvailableVersions,
  });

  // Versions to display in charts. Unlike Aurora (which uses filters.versions),
  // Iceberg needs explicit computation because filters.versions = undefined when all versions selected
  const displayedVersions = useMemo(
    () =>
      // If specific versions selected, show those
      // Otherwise show all available versions
      allAvailableVersions.length === 0
        ? undefined
        : (debouncedSelectedVersions ?? allAvailableVersions),
    [debouncedSelectedVersions, allAvailableVersions],
  );
  const handleVersionChange = (newSelection: string[] | null) => {
    setSelectedVersions(
      computeSelectedVersions(newSelection, allAvailableVersions),
    );
  };

  const jobs = useJobs({
    baseUrl: workspace.base_url ?? "",
    flowId: flow.id,
  });
  const hasJobs = jobs.data && jobs.data?.length > 0;

  return (
    <>
      <SubHeader
        title="Performance"
        titleAction={
          hasJobs && (
            <PerformanceSourcePill
              value={source}
              onChange={(source) => {
                setSource(source);
              }}
            />
          )
        }
        paddedParent
      >
        <SubHeader.DatePicker
          rangeLimitInDays={30}
          value={dateRangePickerValue}
          onChange={onDateRangePickerChange}
        />
        {options && (
          <SubHeaderFilter
            dataLoc="version-filter"
            disabled={options.length < 1 || source === PerformanceSource.JOBS}
            options={options}
            selected={
              selectedVersions === undefined
                ? allAvailableVersions
                : selectedVersions
            }
            tooltip={(() => {
              if (options.length < 1) {
                return "There is no data to filter for the selected dates";
              }
              return source === PerformanceSource.JOBS
                ? "Version filter is not available for Decisions from Jobs"
                : "";
            })()}
            multiple
            onChange={handleVersionChange}
          />
        )}
        <SubHeader.Button
          disabled={refreshing}
          icon={faRefresh}
          spin={refreshing}
          tooltip={
            dataUpdatedAt
              ? `Last updated at ${formatDate(dataUpdatedAt, "h:mm aaa")}`
              : "Refetch"
          }
          onClick={refresh}
        />
      </SubHeader>
      <div
        className={twJoin(
          "mx-auto flex w-full min-w-[1000px] flex-col gap-6",
          tableWidth,
        )}
      >
        <OverviewPane filters={filters} source={source} />

        {source === PerformanceSource.API && (
          <>
            {/* Pass data to chart, to avoid refetching within component on initial fetch */}
            <DecisionsChart
              colors={colors}
              decisionsQuery={decisions}
              displayedVersions={displayedVersions}
              filters={filters}
            />

            <ErrorsChart filters={filters} />

            <LatencyChart
              colors={colors}
              displayedVersions={displayedVersions}
              filters={filters}
            />
          </>
        )}
      </div>
    </>
  );
};
